/**
 * Scoped re-measure helper for MenuContainer (EID-62).
 *
 * Replaces the old MutationObserver hack that dispatched a global window
 * 'resize' on every DOM mutation under the menu. A ResizeObserver feeds box
 * sizes into a {@link ScopedResizeDispatcher}, which dispatches a single window
 * 'resize' per animation frame and only when the box actually changed size.
 *
 * Descendant components (grid Node, deck map, Vega panel) still re-measure on
 * the window 'resize' event, so the re-measure contract is preserved while the
 * per-mutation storm is eliminated: a ResizeObserver fires only on real
 * geometry changes, not on legend-text churn or attribute flips.
 *
 * Copyright: Your Name. Apache 2.0
 */

export interface ScopedResizeDispatcherOptions {
  /** Dispatch the synthetic resize. Defaults to a window 'resize' event. */
  dispatch?: () => void;
  /** Schedule a callback for the next frame. Defaults to requestAnimationFrame. */
  schedule?: (cb: () => void) => number;
  /** Cancel a scheduled callback. Defaults to cancelAnimationFrame. */
  cancel?: (handle: number) => void;
}

const defaultDispatch = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('resize'));
  }
};

const defaultSchedule = (cb: () => void): number =>
  typeof requestAnimationFrame !== 'undefined'
    ? requestAnimationFrame(cb)
    : (setTimeout(cb, 0) as unknown as number);

const defaultCancel = (handle: number): void => {
  if (typeof cancelAnimationFrame !== 'undefined') {
    cancelAnimationFrame(handle);
  } else {
    clearTimeout(handle as unknown as ReturnType<typeof setTimeout>);
  }
};

/**
 * Coalesces a burst of box-size reports into at most one synthetic resize per
 * frame, suppressing reports that don't change the observed dimensions.
 */
export class ScopedResizeDispatcher {
  private lastWidth = -1;
  private lastHeight = -1;
  private frame = 0;
  private readonly dispatch: () => void;
  private readonly schedule: (cb: () => void) => number;
  private readonly cancel: (handle: number) => void;

  constructor(options: ScopedResizeDispatcherOptions = {}) {
    this.dispatch = options.dispatch ?? defaultDispatch;
    this.schedule = options.schedule ?? defaultSchedule;
    this.cancel = options.cancel ?? defaultCancel;
  }

  /**
   * Report a newly observed box size. Dispatches a synthetic resize (coalesced
   * to one per frame) only when width or height changed since the last report.
   */
  report(width: number, height: number): void {
    const w = Math.round(width);
    const h = Math.round(height);
    if (w === this.lastWidth && h === this.lastHeight) return;
    this.lastWidth = w;
    this.lastHeight = h;
    this.scheduleDispatch();
  }

  /**
   * Force a coalesced re-measure dispatch regardless of the observed box size.
   *
   * Used for semantic events (e.g. a same-type menu tab swap) where menuRoot's
   * own box may be unchanged — it is parent-driven, flex-grow/overflow-bounded —
   * yet the *content* swapped, so descendants must re-measure their own boxes.
   * Still coalesced to one dispatch per frame, so rapid tab cycling can't storm.
   */
  scheduleDispatch(): void {
    if (this.frame) return;
    this.frame = this.schedule(() => {
      this.frame = 0;
      this.dispatch();
    });
  }

  /** Cancel any pending dispatch. Call on cleanup. */
  dispose(): void {
    if (this.frame) {
      this.cancel(this.frame);
      this.frame = 0;
    }
  }
}

/** Handle returned by {@link createMenuResizeWiring}. */
export interface MenuResizeWiring {
  /** Begin observing the menu container's box geometry. Idempotent per call. */
  observe(menuRoot: Element): void;
  /**
   * Notify that the active menu node swapped. Forces a coalesced re-measure
   * even when the container box is unchanged (parent-driven flex layout).
   */
  onActiveNodeChange(): void;
  /** Disconnect the observer and cancel any pending dispatch. */
  dispose(): void;
}

/**
 * Wire a {@link ScopedResizeDispatcher} to a ResizeObserver and the active-node
 * swap event, exactly as MenuContainer (node.tsx) does. Extracted so the
 * re-measure contract — geometry change *and* tab swap both dispatch, but
 * non-geometry DOM churn does not — is unit-testable without mounting Solid.
 *
 * `ResizeObserverCtor` defaults to the global; tests inject a controllable stub
 * (jsdom has no ResizeObserver). If unavailable, `observe` is a no-op.
 */
export function createMenuResizeWiring(
  dispatcher: ScopedResizeDispatcher,
  ResizeObserverCtor:
    typeof ResizeObserver | undefined = typeof ResizeObserver !== 'undefined'
    ? ResizeObserver
    : undefined,
): MenuResizeWiring {
  let observer: ResizeObserver | undefined;
  return {
    observe(menuRoot: Element): void {
      if (!ResizeObserverCtor) return;
      // EID-110 (M6): a re-observe (e.g. remount) overwrote `observer` without
      // disconnecting the previous instance, leaking a live ResizeObserver
      // (and its callback closure) per call.
      observer?.disconnect();
      observer = new ResizeObserverCtor((entries) => {
        const rect = entries[0]?.contentRect;
        if (!rect) return;
        dispatcher.report(rect.width, rect.height);
      });
      observer.observe(menuRoot);
    },
    onActiveNodeChange(): void {
      dispatcher.scheduleDispatch();
    },
    dispose(): void {
      observer?.disconnect();
      dispatcher.dispose();
    },
  };
}
